import React from "react";
import { getContainerBarSegments } from "../../../../utils/containerBarSegments";

/**
 * Renders stacked percentage bar charts for container occupancy by size.
 *
 * @param {Object} props
 * @param {Object|null|undefined} props.oNASiteData - Container stats keyed by size
 * @returns {JSX.Element|null}
 */
const BarChart = ({ oNASiteData }) => {
  if (!oNASiteData) {
    return null;
  }

  const toPercent = (ratio) => ratio * 100;

  return (
    <div className="chart-container">
      {/* Legend */}
      <div className="legend">
        <div className="legend-item">
          <span className="box occupied"></span> Occupied
        </div>
        <div className="legend-item">
          <span className="box available"></span> Available
        </div>
        <div className="legend-item">
          <span className="box maintenance"></span> Under maintenance
        </div>
        <div className="legend-item">
          <span className="box capacity"></span> Capacity
        </div>
        <div className="legend-item">
          <span className="box not-sold"></span> Not Sold
        </div>
      </div>

      {/* Bars */}
      {Object.entries(oNASiteData).map(
        ([key, { capacity, available, occupied, maintenance = 0 }]) => {
          const segments = getContainerBarSegments({
            capacity,
            available,
            occupied,
            maintenance,
          });
          const occupiedWidth = toPercent(segments.occupiedRatio);
          const availableWidth = toPercent(segments.availableRatio);
          const maintenanceWidth = toPercent(segments.maintenanceRatio);
          const capacityWidth = toPercent(segments.capacityRatio);
          const notSoldWidth = segments.isNotSold ? 100 : 0;
          const { remainingCapacity } = segments;

          return (
            <div key={key} className="bar-group">
              <div className="barLabel">{key.replace("_", " ")}</div>
              <div className="bar">
                {occupied > 0 && (
                  <div
                    className="occupied"
                    style={{ width: `${occupiedWidth}%` }}
                  ></div>
                )}
                {available > 0 && (
                  <div
                    className="available"
                    style={{ width: `${availableWidth}%` }}
                  ></div>
                )}
                {maintenance > 0 && (
                  <div
                    className="maintenance"
                    style={{ width: `${maintenanceWidth}%` }}
                  ></div>
                )}
                {capacity !== null && remainingCapacity > 0 && (
                  <div
                    className="capacity"
                    style={{ width: `${capacityWidth}%` }}
                  ></div>
                )}
                {capacity === null && (
                  <div
                    className="not-sold"
                    style={{ width: `${notSoldWidth}%` }}
                  ></div>
                )}
              </div>
              <div className="values w-100">
                {occupied > 0 && (
                  <span
                    className="occupiedT"
                    style={{ width: `${occupiedWidth}%`, textAlign: "start" }}
                  >
                    <strong>{occupied}</strong>
                  </span>
                )}
                {available > 0 && (
                  <span
                    className="availableT"
                    style={{
                      width: `${availableWidth}%`,
                      textAlign: "center",
                    }}
                  >
                    <strong>{available}</strong>
                  </span>
                )}
                {maintenance > 0 && (
                  <span
                    className="maintenanceT"
                    style={{
                      width: `${maintenanceWidth}%`,
                      textAlign: "center",
                    }}
                  >
                    <strong>{maintenance}</strong>
                  </span>
                )}
                {capacity !== null && remainingCapacity > 0 && (
                  <span
                    className="capacityT"
                    style={{ width: `${capacityWidth}%`, textAlign: "end" }}
                  >
                    <strong> {capacity}</strong>
                  </span>
                )}
                {capacity === null && (
                  <span
                    style={{ width: `${notSoldWidth}%`, textAlign: "center" }}
                  >
                    Not Sold
                  </span>
                )}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default BarChart;
